import React, { useMemo } from 'react';
import {
  Alert,
  Divider,
  Empty,
  Flex,
  Pagination,
  Tag,
  Typography,
  theme,
} from '@duxton-web/ui';
import { CalendarOutlined } from '@duxton-web/icons';
import CommsTraceTimelineCard from './CommsTraceTimelineCard';
import { journeyKey } from './commsTraceStatus';
import { groupJourneysByEventDate } from './commsTraceTimeline';
import type { CommsTraceJourney } from './types';

type Props = {
  journeys: CommsTraceJourney[];
  total: number;
  truncated: boolean;
  page: number;
  pageSize: number;
  timezone: string;
  timezoneCode?: string;
  onSelectJourney: (journey: CommsTraceJourney) => void;
  onPageChange: (page: number) => void;
};

const CommsTraceUserTimeline: React.FC<Props> = ({
  journeys,
  total,
  truncated,
  page,
  pageSize,
  timezone,
  timezoneCode,
  onSelectJourney,
  onPageChange,
}) => {
  const { token } = theme.useToken();
  const dayGroups = useMemo(
    () => groupJourneysByEventDate(journeys, timezone),
    [journeys, timezone],
  );

  return (
    <Flex vertical gap={token.marginMD} style={{ width: '100%' }}>
      {truncated && (
        <Alert
          type="warning"
          showIcon
          message="Results hit the event cap. The oldest events were dropped, so this user's oldest journeys may be missing or incomplete."
        />
      )}
      {!journeys.length && <Empty description="No journeys for this user on this page." />}
      {dayGroups.map((group, index) => (
        <React.Fragment key={group.dateKey}>
          {index > 0 && <Divider style={{ margin: `${token.marginXS}px 0` }} />}
          <Flex vertical gap={token.marginXS}>
            <Flex align="center" justify="space-between" gap={token.marginSM}>
              <Flex align="center" gap={token.marginXXS}>
                <CalendarOutlined />
                <Typography.Text strong>{group.label}</Typography.Text>
              </Flex>
              <Tag
                bordered={true}
                color="purple"
              >
                {group.count} {group.count === 1 ? 'comm' : 'comms'}
              </Tag>
            </Flex>
            <Flex vertical gap={token.marginXS}>
              {group.journeys.map((journey, journeyIndex) => (
                <CommsTraceTimelineCard
                  key={journeyKey(journey, journeyIndex)}
                  journey={journey}
                  timezone={timezone}
                  timezoneCode={timezoneCode}
                  onClick={() => onSelectJourney(journey)}
                />
              ))}
            </Flex>
          </Flex>
        </React.Fragment>
      ))}
      {total > pageSize && (
        <Flex justify="flex-end">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger={false}
            onChange={onPageChange}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default CommsTraceUserTimeline;
